import type { ReactNode, SelectHTMLAttributes } from 'react';
import { useId } from 'react';
import { cn } from './cn.js';
import { useRequiredMark } from './Form.js';
import { Icon } from './Icon.js';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  error?: string | null;
  help?: ReactNode;
  /** Esconde o rótulo visualmente e o mantém para leitor de tela. */
  hiddenLabel?: boolean;
  children: ReactNode;
}

/**
 * Escolha de uma opção entre poucas.
 *
 * `<select>` nativo, e não um menu desenhado: no telefone o nativo abre a roda
 * do sistema, funciona com teclado sem uma linha de código e não tem estado de
 * abertura para dessincronizar. Um combobox acessível escrito à mão é uma
 * semana de trabalho para chegar pior — e o `DESIGN.md` só manda buscar
 * primitivo quando há foco preso ou navegação por teclado que o nativo não dá.
 *
 * Sobe para `packages/ui` pela regra do `PADROES.md`: a terceira tela precisou.
 * O filtro de situação, o de categoria e o de responsável de demandas são três
 * de uma vez, e o `Layout` já tinha o seu escrito à mão — dois desenhos para a
 * mesma peça, que é o que a decisão 3 do `DESIGN.md` proíbe. O do `Layout` fica
 * onde está: ele vive dentro do navy da moldura, onde a paleta é outra.
 *
 * A seta é desenhada por nós porque a nativa muda de forma entre navegadores, e
 * a assinatura do deck não pode depender de qual navegador a pessoa abriu.
 */
export function Select({
  label,
  error,
  help,
  hiddenLabel = false,
  children,
  className,
  id,
  required,
  ...props
}: SelectProps) {
  const generated = useId();
  const idField = id ?? generated;
  const idError = `${idField}-erro`;
  const idHelp = `${idField}-ajuda`;

  // Ver `Field`: o aviso de obrigatórios do rodapé é contado, não escrito.
  useRequiredMark(required);

  const descricao = [help ? idHelp : null, error ? idError : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={idField}
        className={cn(
          'text-xs font-medium text-texto',
          // some da tela, continua no leitor: um filtro numa barra de filtros
          // não precisa do rótulo repetido acima de cada caixa
          hiddenLabel && 'sr-only',
        )}
      >
        {label}
        {required && (
          <>
            <span aria-hidden="true" className="ml-0.5 text-erro">
              *
            </span>
            <span className="sr-only"> (obrigatório)</span>
          </>
        )}
      </label>

      <div className="relative">
        <select
          {...props}
          id={idField}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={descricao || undefined}
          className={cn(
            'h-9 w-full appearance-none rounded-borda border bg-superficie pl-3 pr-9 text-xs',
            'text-texto pointer-coarse:h-11',
            'transition-[border-color,background-color]',
            'disabled:cursor-not-allowed disabled:bg-superficie-suave disabled:opacity-60',
            error ? 'border-erro' : 'border-borda-forte hover:border-texto-suave',
            className,
          )}
        >
          {children}
        </select>
        <Icon
          nome="seta-baixo"
          size={16}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-texto-suave"
        />
      </div>

      {help && (
        <p id={idHelp} className="text-rotulo text-texto-suave">
          {help}
        </p>
      )}
      {error && (
        <p id={idError} className="flex items-center gap-1 text-xs font-medium text-erro">
          <Icon nome="atencao" size={16} />
          {error}
        </p>
      )}
    </div>
  );
}
